import { CREVERSE_HEAD } from '@/constant/creverseHead'

type Brand = 'default' | 'creverse'

type CapturedLink = {
  el: HTMLLinkElement
  href: string | null
  type: string | null
}

type CapturedMeta = {
  el: HTMLMetaElement
  content: string | null
}

type DefaultHead = {
  title: string
  icons: CapturedLink[]
  appleTouchIcons: CapturedLink[]
  appNames: CapturedMeta[]
}

/**
 * 기본값을 상수로 못 두는 이유: 탭 제목은 dev 브랜치 접두사와 브랜드(매쓰플랫/스쿨플랫)가
 * 빌드 타임에 조합된 값이라 런타임에서 재구성할 수 없다. 첫 호출 때 실제 head 를 캡처해 원복에 쓴다.
 */
let defaultHead: DefaultHead | null = null
let appliedBrand: Brand | null = null

const captureLinks = (selector: string): CapturedLink[] =>
  Array.from(document.querySelectorAll<HTMLLinkElement>(selector)).map((el) => ({
    el,
    href: el.getAttribute('href'),
    type: el.getAttribute('type'),
  }))

const captureMetas = (selector: string): CapturedMeta[] =>
  Array.from(document.querySelectorAll<HTMLMetaElement>(selector)).map((el) => ({
    el,
    content: el.getAttribute('content'),
  }))

const setAttr = (el: Element, name: string, value: string | null) => {
  if (value === null) el.removeAttribute(name)
  else el.setAttribute(name, value)
}

const setLink = (el: HTMLLinkElement, href: string | null, type: string | null) => {
  setAttr(el, 'href', href)
  setAttr(el, 'type', type)
}

export const applyBrandHead = (brand: Brand) => {
  // 라우터 구독이 브랜드 변화 없이도 반복 호출하므로, 같은 브랜드면 head 를 다시 쓰지 않는다 (href 재대입은 파비콘 재요청을 유발).
  if (brand === appliedBrand) return
  appliedBrand = brand

  if (!defaultHead) {
    defaultHead = {
      title: document.title,
      icons: captureLinks('link[rel="icon"]'),
      appleTouchIcons: captureLinks('link[rel^="apple-touch-icon"]'),
      appNames: captureMetas(
        'meta[name="apple-mobile-web-app-title"], meta[name="application-name"]',
      ),
    }
  }

  if (brand === 'creverse') {
    document.title = CREVERSE_HEAD.title
    // 기존 type(image/png 등)이 크레버스 자산 포맷과 다를 수 있어 지우고 브라우저 스니핑에 맡긴다.
    defaultHead.icons.forEach(({ el }) => setLink(el, CREVERSE_HEAD.favicon, null))
    defaultHead.appleTouchIcons.forEach(({ el }) => setLink(el, CREVERSE_HEAD.appleTouchIcon, null))
    defaultHead.appNames.forEach(({ el }) => setAttr(el, 'content', CREVERSE_HEAD.title))
    return
  }

  document.title = defaultHead.title
  defaultHead.icons.forEach(({ el, href, type }) => setLink(el, href, type))
  defaultHead.appleTouchIcons.forEach(({ el, href, type }) => setLink(el, href, type))
  defaultHead.appNames.forEach(({ el, content }) => setAttr(el, 'content', content))
}
